import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BadgeCheck, AlertTriangle, Clock, Ban, HelpCircle, ShieldOff, ShieldCheck, Loader2, X } from 'lucide-react';
import { US_WAREHOUSE_CODES } from '../../../shared/utils/usWarehouses'; // CHA-302

/**
 * CpscBadges — CHA-179 badges consumer. Self-contained CPSC badge layer for Production2Dashboard, kept OUT
 * of that file so the daily-ops surface takes only tiny additive insertions (§9). Exposes:
 *   - useCpscBadges(userProfile, detailData) → { enabled, loading, statusByStyle, overrideSet, refresh, set/removeOverride }
 *   - <CpscBadgeCell row ctx onOverride/> — the inline badge for a style cell (decision 2)
 *   - isRowUrgent(row, ctx) — for the URGENT row highlight (decision 9)
 *   - <CpscOverrideModal .../> — per-style override capture (decisions 10–14)
 *
 * Feature-flag gated on /cpsc_config/{tenantId}.badgesEnabled (decision 6; client-readable, no rules change).
 * Engine call = mount + manual refresh (decision 3), batched ≤500 styles. Unmatched styles → grey "?"
 * (decision 5). URGENT = shipped + needs-cert + not-certified + not-overridden (decision 9).
 */
const s = (v) => String(v ?? '').trim();
const isShipped = (stage) => s(stage).toUpperCase() === 'SHIPPED';
const CHUNK = 500;

// CPSC badge SCOPE (CHA-179): badges apply ONLY to US-CPLAN production orders — most other rows don't need
// CPSC and badging them is false noise. Hardcoded now; becomes operator-editable config in CHA-195. Out-of-
// scope rows get NO badge (blank cell), are never urgent, and aren't sent to the engine.
const SCOPE_PROD_TYPE = 'CPLAN';                                            // pod_PrdTyp
// CHA-302 — was a hand-written 9-code Set MISSING BCC, so the badge surface disagreed with the
// warehouses every other module scopes to. Now the one shared vocabulary.
const SCOPE_CARRIERS = new Set(US_WAREHOUSE_CODES); // shp2
export function isInCpscScope(row) {
  return s(row && row.prodType).toUpperCase() === SCOPE_PROD_TYPE && SCOPE_CARRIERS.has(s(row && row.shipCode).toUpperCase());
}

// Engine status → pill. (certified|awaiting|submitted|in_progress|none|na|exempt) + derived urgent/override/unknown.
const BADGE = {
  certified: { tone: 'green', icon: BadgeCheck, label: 'CPSC' },
  awaiting: { tone: 'blue', icon: Clock, label: 'Awaiting' },
  submitted: { tone: 'blue', icon: Clock, label: 'Submitting' },
  in_progress: { tone: 'blue', icon: Clock, label: 'In progress' },
  none: { tone: 'amber', icon: AlertTriangle, label: 'Missing' },
  failed: { tone: 'red', icon: AlertTriangle, label: 'Failed' }, // submission_error/certification_failed — actionable, NOT "Missing" (Codex P2 #83)
  review: { tone: 'yellow', icon: AlertTriangle, label: 'Verify' }, // children's — may need a CPC, verify
  na: { tone: 'slate', icon: Ban, label: 'N/A' },
  exempt: { tone: 'teal', icon: ShieldCheck, label: 'EXEMPT' }, // CHA-222 PR2 — 16 CFR 1610.1(d) flammability exemption; no GCC required
  not_subject: { tone: 'slate', icon: Ban, label: 'NOT SUBJECT' }, // CHA-540 — Target TTS determination, distinct from exemption
};
const TONE = {
  green: 'text-green-700 border-green-200 bg-green-50', amber: 'text-amber-700 border-amber-200 bg-amber-50',
  red: 'text-red-700 border-red-200 bg-red-50', blue: 'text-blue-700 border-blue-200 bg-blue-50',
  yellow: 'text-yellow-800 border-yellow-300 bg-yellow-50', slate: 'text-slate-500 border-slate-200 bg-white',
  teal: 'text-teal-700 border-teal-200 bg-teal-50', // CHA-222 PR2 — distinct from certified-green + needs-cert-amber/red
};
const Pill = ({ tone, icon: Icon, label, title, onClick }) => (
  <span
    title={title}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    className={`inline-flex items-center gap-1 border rounded-full px-1.5 py-0.5 text-[10px] font-medium align-middle ${TONE[tone]} ${onClick ? 'cursor-pointer hover:brightness-95' : ''}`}
  >
    <Icon className="h-3 w-3" /> {label}
  </span>
);

export function useCpscBadges(userProfile, detailData) {
  const role = userProfile?.role;
  const isAdmin = role === 'platform_owner' || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [enabled, setEnabled] = useState(false);
  const [statusByStyle, setStatusByStyle] = useState({});
  const [overrideSet, setOverrideSet] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadSeq = useRef(0);

  const styles = useMemo(
    () => [...new Set((detailData || []).filter(isInCpscScope).map((r) => s(r?.style)).filter(Boolean))],
    [detailData],
  );

  const load = useCallback(async () => {
    if (!isAdmin) { setEnabled(false); return; }
    const seq = (loadSeq.current += 1);
    setLoading(true); setError(null);
    try {
      const db = getFirestore();
      const cfg = await getDoc(doc(db, 'cpsc_config', tenantId));
      const on = !!(cfg.exists() && cfg.data() && cfg.data().badgesEnabled);
      if (seq !== loadSeq.current) return;
      setEnabled(on);
      if (!on || !styles.length) { setStatusByStyle({}); setOverrideSet(new Set()); return; }

      const fns = getFunctions();
      const statusCall = httpsCallable(fns, 'getCpscStatusForStyles', { timeout: 150000 }); // CHA-382 / CHA-528 — server's 120s window + 30s transport headroom
      const chunks = [];
      for (let i = 0; i < styles.length; i += CHUNK) chunks.push(styles.slice(i, i + CHUNK));
      const [results, ovr] = await Promise.all([
        Promise.all(chunks.map((c) => statusCall({ styleNos: c, tenantId }))),
        httpsCallable(fns, 'listCpscStyleOverrides')({ tenantId }),
      ]);
      if (seq !== loadSeq.current) return;
      const merged = {};
      results.forEach((r) => Object.assign(merged, (r && r.data && r.data.statuses) || {}));
      setStatusByStyle(merged);
      setOverrideSet(new Set(((ovr && ovr.data && ovr.data.styles) || []).map(s)));
    } catch (e) {
      if (seq !== loadSeq.current) return;
      console.error('CPSC badges load failed', e); // don't swallow (§8.3)
      setError('Could not load CPSC badges.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [isAdmin, tenantId, styles]);

  useEffect(() => { load(); }, [load]);

  const setOverride = useCallback(async (style, reason) => {
    await httpsCallable(getFunctions(), 'setCpscStyleOverride')({ style: s(style), reason: s(reason), tenantId });
    setOverrideSet((p) => new Set(p).add(s(style)));
  }, [tenantId]);

  const removeOverride = useCallback(async (style) => {
    await httpsCallable(getFunctions(), 'removeCpscStyleOverride')({ style: s(style), tenantId });
    setOverrideSet((p) => { const n = new Set(p); n.delete(s(style)); return n; });
  }, [tenantId]);

  return { enabled, isAdmin, loading, error, statusByStyle, overrideSet, refresh: load, setOverride, removeOverride };
}

// PURE — URGENT when a shipped PO carries a needs-cert style that isn't certified and isn't overridden.
export function isRowUrgent(row, ctx) {
  if (!ctx || !ctx.enabled || !isInCpscScope(row)) return false;
  const style = s(row && row.style);
  if (!style || ctx.overrideSet.has(style)) return false;
  const info = ctx.statusByStyle[style];
  if (!info) return false; // unknown ("?") never screams urgent
  // URGENT only for a HARD required-missing (status 'none' + applicable) — not 'review' (soft verify) or '?'.
  return isShipped(row && row.stage) && info.status === 'none' && info.cpscApplicable === true;
}

// Row-highlight predicate — a shipped URGENT missing cert (override-able) OR a shipped FAILED filing
// (submission_error/certification_failed — needs re-submit). Both warrant the red attention row; the per-style
// badge distinguishes URGENT vs Failed. Kept separate from isRowUrgent so 'failed' doesn't hijack the URGENT/
// override pill (Codex P2 #83).
export function isRowAttention(row, ctx) {
  if (isRowUrgent(row, ctx)) return true;
  if (!ctx || !ctx.enabled || !isInCpscScope(row)) return false;
  const style = s(row && row.style);
  if (!style || ctx.overrideSet.has(style)) return false;
  const info = ctx.statusByStyle[style];
  return !!info && isShipped(row && row.stage) && info.status === 'failed';
}

// The inline badge for one row's style cell.
export function CpscBadgeCell({ row, ctx, onOverride }) {
  if (!ctx || !ctx.enabled || !isInCpscScope(row)) return null; // out-of-scope → blank cell (CHA-179 scope)
  const style = s(row && row.style);
  if (!style) return null;
  const info = ctx.statusByStyle[style];
  if (ctx.loading && !info) return <span className="inline-block w-12 h-3.5 rounded-full bg-slate-100 animate-pulse align-middle" aria-label="Loading CPSC status" />;
  // Unknown → grey "?" (decision 5): either the engine returned nothing, OR no HTS on file (cpscApplicable
  // null) with no cert. "We can't determine if CPSC applies" is NOT the same as amber "Missing".
  if (!info || (info.cpscApplicable == null && info.status === 'none')) {
    return <Pill tone="slate" icon={HelpCircle} label="?" title="No HTS on file for this style — can't determine if a CPSC certificate is required" />;
  }

  const overridden = ctx.overrideSet.has(style);
  // Show the (removable) Override pill whenever an override is active for this style — not only while the row
  // currently satisfies the urgent predicate. An override lingers server-side + suppresses future URGENT, so
  // it must stay visible/removable even after the row becomes non-urgent or certified (Codex P2 #83).
  if (overridden) {
    return <Pill tone="slate" icon={ShieldOff} label="Override" title="Approved to ship without a certificate — click to review/remove" onClick={onOverride ? () => onOverride(style) : undefined} />;
  }
  if (isRowUrgent(row, ctx)) {
    return <Pill tone="red" icon={AlertTriangle} label="URGENT" title="Shipped without a certificate — click to override" onClick={onOverride ? () => onOverride(style) : undefined} />;
  }
  // CHA-222 PR2 — EXEMPT (16 CFR 1610.1(d)): teal badge with the exemption-path label in the tooltip detail.
  // No certType in the title (an exempt style needs no GCC), and the path (A weight-based / B fiber-based).
  if (info.status === 'exempt') {
    const e = BADGE.exempt;
    const path = s(info.exemptionPath);
    const detail = path === 'A' ? ' (Path A)' : path === 'B' ? ' (Path B)' : '';
    return <Pill tone={e.tone} icon={e.icon} label={e.label} title={`EXEMPT${detail} — flammability exemption on file (16 CFR 1610.1(d)); no CPSC certificate required`} />;
  }
  if (info.status === 'not_subject') {
    const n = BADGE.not_subject;
    return <Pill tone={n.tone} icon={n.icon} label={n.label} title="Target TTS confirms this adult product is not subject to CPSC certification rules; no CPC or GCC required" />;
  }
  const b = BADGE[info.status] || BADGE.none;
  const title = `${b.label}${info.certType ? ` (${info.certType})` : ''}${info.sourceChain && info.sourceChain.hts ? ` · HTS ${info.sourceChain.hts}` : ''}`;
  return <Pill tone={b.tone} icon={b.icon} label={b.label} title={title} />;
}

// Per-style override capture (decision 12): confirmation + optional reason; or remove if already overridden.
export function CpscOverrideModal({ style, ctx, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setReason(''); setErr(null); setBusy(false); }, [style]);
  if (!style) return null;
  const overridden = ctx.overrideSet.has(s(style));

  const act = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); onClose(); }
    catch (e) { console.error('Override action failed', e); setErr(e && e.message ? e.message : 'Action failed.'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="text-base font-semibold text-slate-800">{overridden ? 'Remove CPSC override' : 'Approve to ship without a certificate'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-slate-600">
          Style <span className="font-mono font-medium">{style}</span>.{' '}
          {overridden
            ? 'Removing the override restores the 🔴 URGENT badge on shipped POs of this style.'
            : 'This silences the URGENT badge for every PO of this style until removed. Use only if you have confirmed the product may ship without a CPSC certificate.'}
        </p>
        {!overridden && (
          <textarea
            className="w-full h-20 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Reason (optional) — recorded in the audit trail"
            value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Override reason"
          />
        )}
        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          {overridden ? (
            <button onClick={() => act(() => ctx.removeOverride(style))} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Remove override
            </button>
          ) : (
            <button onClick={() => act(() => ctx.setOverride(style, reason))} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve to ship
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
